import { HandIcon } from '@c/icons';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import Button from '@ui/Button';
import { formatCurrency } from '@util/index';
import { OfferDocument } from '@util/types/firestore/offers';
import React, { useState } from 'react';
import BaseModal from './BaseModal';

interface CounterOfferModalProps {
  isOpen: boolean;
  offer: OfferDocument;
  productPrice: number;
  isSeller: boolean;
  onSubmit: (newPrice: number, message?: string) => Promise<void>;
  dismiss: () => void;
}

function CounterOfferModal({
  isOpen,
  offer,
  productPrice,
  isSeller,
  onSubmit,
  dismiss,
}: CounterOfferModalProps) {
  const [price, setPrice] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getNumber = (numStr: string) =>
    numStr ? Number(numStr.replace(/,/g, '')) : 0;

  const currentPrice = offer.price || offer.current_price || 0;

  // Find buyer's last offer price from counter history or original price
  const getBuyerLastPrice = (): number => {
    if (offer.counter_history && offer.counter_history.length > 0) {
      // Find buyer's most recent counter in history
      for (let i = offer.counter_history.length - 1; i >= 0; i--) {
        if (offer.counter_history[i].by === 'buyer') {
          return offer.counter_history[i].price;
        }
      }
    }
    // Fall back to original price (buyer's initial offer)
    return offer.original_price || offer.price || 0;
  };

  const handleSubmit = async () => {
    setError('');
    const newPrice = getNumber(price);

    if (!newPrice || newPrice <= 0) {
      setError('Please enter a valid price');
      return;
    }

    if (isSeller) {
      if (newPrice > productPrice) {
        setError(
          `Counter price cannot exceed the listing price of ${formatCurrency(productPrice)}`
        );
        return;
      }
      if (newPrice <= 0) {
        setError('Counter price must be greater than $0');
        return;
      }
    } else {
      // Buyer must offer MORE than their previous offer (negotiate up)
      const buyerLastPrice = getBuyerLastPrice();
      if (newPrice <= buyerLastPrice) {
        setError(
          `Counter price must be higher than your previous offer of ${formatCurrency(buyerLastPrice)}`
        );
        return;
      }
      if (newPrice > productPrice) {
        setError(
          `Counter price cannot exceed the listing price of ${formatCurrency(productPrice)}`
        );
        return;
      }
    }

    setLoading(true);
    try {
      await onSubmit(newPrice, message || undefined);
      setPrice('');
      setMessage('');
      dismiss();
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to submit counter offer';
      setError(errorMessage);
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    setPrice('');
    setMessage('');
    setError('');
    dismiss();
  };

  return (
    <BaseModal isOpen={isOpen} dismiss={handleDismiss} title="Counter Offer" width="fit-content">
      <div className="flex w-[40rem] flex-col gap-[2.4rem] p-[1.6rem]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[1.2rem]">
            <HandIcon />
            <div className="flex flex-col">
              <span className="text-[1.4rem] text-brand-gray">Current Offer</span>
              <span className="text-[1.8rem] font-semibold">
                {formatCurrency(currentPrice)}
              </span>
            </div>
          </div>
          <div className="flex flex-col text-right">
            <span className="text-[1.4rem] text-brand-gray">Listed Price</span>
            <span className="text-[1.8rem] font-semibold text-brand-dark-gray">
              {formatCurrency(productPrice)}
            </span>
          </div>
        </div>

        {offer.counter_history && offer.counter_history.length > 0 && (
          <div className="flex flex-col gap-[0.8rem] rounded-[0.8rem] bg-brand-lightest-gray p-[1.2rem]">
            <div className="flex items-center justify-between">
              <span className="text-[1.3rem] font-medium text-brand-gray">
                Negotiation History
              </span>
              {/* Only show counter limit warning when approaching the limit (8+) */}
              {offer.counter_history.length >= 8 && (
                <span className="text-[1.2rem] text-orange-500 font-medium">
                  {offer.counter_history.length} of 10 counters
                </span>
              )}
            </div>
            {offer.counter_history.map((counter, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-[1.3rem]"
              >
                <span className="text-brand-dark-gray">
                  {counter.by === 'buyer' ? 'Buyer' : 'Seller'}
                </span>
                <span className="font-medium">
                  {formatCurrency(counter.price)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Warning when counter limit is reached */}
        {offer.counter_history && offer.counter_history.length >= 10 && (
          <div className="rounded-[0.8rem] border border-red-300 bg-red-50 p-[1.2rem]">
            <span className="text-[1.3rem] text-red-800">
              Maximum counter offers reached (10). Please accept or decline this offer,
              or create a new offer if needed.
            </span>
          </div>
        )}

        <FormLabel value="Your Counter Offer" required>
          <FormCurrencyInputFancy
            full
            placeholder="0.00"
            error={!!error}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setError('');
              setPrice(e.target.value);
            }}
          />
        </FormLabel>

        {/* Warning when seller counters below buyer's offer */}
        {isSeller && getNumber(price) > 0 && getNumber(price) < currentPrice && (
          <div className="rounded-[0.8rem] border border-yellow-300 bg-yellow-50 p-[1.2rem]">
            <span className="text-[1.3rem] text-yellow-800">
              ⚠️ Your counter ({formatCurrency(getNumber(price))}) is less than the
              buyer&apos;s current offer ({formatCurrency(currentPrice)}). Are you sure you
              want to accept less than offered?
            </span>
          </div>
        )}

        {error && (
          <span className="text-[1.3rem] text-brand-red">{error}</span>
        )}

        <FormLabel value="Message (Optional)">
          <FormInput
            placeholder="Add a message..."
            value={message}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setMessage(e.target.value)
            }
          />
        </FormLabel>

        {isSeller && (
          <p className="text-[1.3rem] text-brand-gray">
            Note: This counter offer is subject to product availability. If the
            item sells before the buyer responds, this offer will be cancelled.
          </p>
        )}

        <div className="flex items-center justify-center gap-[1.6rem]">
          <Button
            type="text"
            text="Cancel"
            onClick={handleDismiss}
            disabled={loading}
          />
          <Button
            type="secondary"
            text="Submit Counter"
            onClick={handleSubmit}
            loading={loading}
          />
        </div>
      </div>
    </BaseModal>
  );
}

export default CounterOfferModal;
